/**
 * User management.
 *
 * Lets an administrator add and manage the people in their own organisation
 * instead of editing the database. Everything shown here is scoped by the
 * server to the signed-in organisation; this page never sends an organisation
 * id, and hides actions the server would refuse (own role, shared accounts,
 * consented external staff) purely as a courtesy — the server enforces all of
 * them regardless.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import {
  Card, Button, Pill, Modal, Field, EmptyState, Skeleton,
  ErrorNotice, StatTile, type Tone,
} from '../components/ui';
import { IconShield } from '../components/icons';
import { ROLES, type Role } from '@opencomply/shared';

interface Member {
  id: string; email: string; firstName: string; lastName: string;
  phone: string | null; location: string | null;
  role: Role; isExternal: boolean; isActive: boolean;
  mfaMethod: string; mustChangePassword: boolean;
  lockedUntil: string | null; lastLoginAt: string | null; addedAt: string;
  /** True when the person also belongs to another organisation. */
  sharedAccount: boolean;
}

/** MFA method → tone. An authenticator app is the stronger factor. */
const MFA_TONE: Record<string, Tone> = { TOTP: 'good', EMAIL_OTP: 'neutral' };

type ConfirmKind = 'password' | 'mfa' | 'remove';

/** A person who was invited and has not signed in yet. */
const isPending = (m: Member) => m.mustChangePassword && !m.lastLoginAt;

export function UsersPage() {
  const { t, i18n } = useTranslation();
  const { allows, session } = useAuth();
  const queryClient = useQueryClient();
  const describeError = useApiError();

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const [confirming, setConfirming] = useState<{ kind: ConfirmKind; member: Member } | null>(null);
  const [notice, setNotice] = useState<{ tone: 'good' | 'warning'; message: string } | null>(null);

  const selfId = session?.user.id;

  const { data, isLoading, error: listError, refetch } = useQuery({
    queryKey: ['users', { search, roleFilter }],
    queryFn: () =>
      api.get<{ items: Member[]; total: number }>(
        `/users${qs({ search, role: roleFilter, pageSize: 100 })}`,
      ),
  });

  const items = data?.items ?? [];

  const formatDate = (iso: string | null) =>
    iso
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso))
      : null;

  const kpis = {
    total: items.length,
    active: items.filter((m) => m.isActive).length,
    pending: items.filter(isPending).length,
  };

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['users'] });

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="oc-h1">{t('users.title')}</h1>
          <p className="oc-muted mt-1">{t('users.subtitle')}</p>
        </div>
        {allows('user.create') && (
          <Button size="sm" variant="primary" onClick={() => { setNotice(null); setInviteOpen(true); }}>
            {t('users.invite')}
          </Button>
        )}
      </header>

      {notice && (
        <div
          role="status"
          className={
            notice.tone === 'good'
              ? 'rounded-xl border border-good/30 bg-good/[0.06] px-4 py-3 text-sm text-ink'
              : 'rounded-xl border border-warning/30 bg-warning/[0.06] px-4 py-3 text-sm text-ink'
          }
        >
          {notice.message}
        </div>
      )}

      {items.length > 0 && (
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          <StatTile label={t('common.total')} value={kpis.total} />
          <StatTile label={t('users.activeCount')} value={kpis.active} />
          <StatTile label={t('users.pendingCount')} value={kpis.pending}
                    tone={kpis.pending > 0 ? 'warning' : undefined} />
        </div>
      )}

      <Card className="!p-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="oc-input sm:max-w-xs" type="search"
            placeholder={t('users.searchPlaceholder')}
            value={search} onChange={(e) => setSearch(e.target.value)}
          />
          <select className="oc-input sm:!w-auto" value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  aria-label={t('users.role')}>
            <option value="">{t('common.all')}</option>
            {ROLES.map((r) => <option key={r} value={r}>{t(`role.${r}`)}</option>)}
          </select>
        </div>
      </Card>

      {isLoading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : listError ? (
        <ErrorNotice message={describeError(listError)} onRetry={() => void refetch()}
                     retryLabel={t('common.retry')} />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<IconShield className="w-10 h-10" />}
            title={t('users.none')}
            action={allows('user.create')
              ? <Button variant="primary" onClick={() => setInviteOpen(true)}>{t('users.invite')}</Button>
              : undefined}
          />
        </Card>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {items.map((m) => {
            const isSelf = m.id === selfId;
            const lastLogin = formatDate(m.lastLoginAt);
            // Why an action is unavailable is explained on the card, so a
            // disabled button is never a mystery.
            const locked = m.isExternal;
            return (
              <li key={m.id}>
                <div className="oc-card p-4 w-full">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <p className="font-medium text-ink truncate">
                        {m.firstName} {m.lastName}
                        {isSelf && <span className="oc-muted font-normal"> · {t('users.you')}</span>}
                      </p>
                      <p className="text-xs text-ink-muted font-mono truncate">{m.email}</p>
                    </div>
                    <Pill tone="neutral" className="shrink-0">{t(`role.${m.role}`)}</Pill>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    <Pill tone={MFA_TONE[m.mfaMethod] ?? 'neutral'}>
                      {t(`users.mfa.${m.mfaMethod}`, { defaultValue: m.mfaMethod })}
                    </Pill>
                    {!m.isActive && <Pill tone="neutral">{t('users.inactive')}</Pill>}
                    {isPending(m) && <Pill tone="warning">{t('users.pending')}</Pill>}
                    {m.isExternal && <Pill tone="neutral">{t('users.external')}</Pill>}
                    {m.lockedUntil && new Date(m.lockedUntil) > new Date() && (
                      <Pill tone="serious">{t('users.locked')}</Pill>
                    )}
                  </div>

                  <p className="text-xs text-ink-muted mb-3">
                    {lastLogin
                      ? t('users.lastLogin', { date: lastLogin })
                      : t('users.neverSignedIn')}
                  </p>

                  {locked && <p className="text-xs text-ink-muted mb-3">{t('users.externalNote')}</p>}

                  {!locked && (allows('user.update') || allows('user.credentials.reset') || allows('user.delete')) && (
                    <div className="flex flex-wrap gap-2">
                      {allows('user.update') && (
                        <Button size="sm" onClick={() => { setNotice(null); setEditing(m); }}>
                          {t('common.edit')}
                        </Button>
                      )}
                      {allows('user.credentials.reset') && !isSelf && m.isActive && (
                        <Button size="sm" onClick={() => { setNotice(null); setConfirming({ kind: 'password', member: m }); }}>
                          {t('users.resetPassword')}
                        </Button>
                      )}
                      {allows('user.credentials.reset') && !isSelf && !m.sharedAccount && (
                        <Button size="sm" onClick={() => { setNotice(null); setConfirming({ kind: 'mfa', member: m }); }}>
                          {t('users.resetMfa')}
                        </Button>
                      )}
                      {allows('user.delete') && !isSelf && (
                        <Button size="sm" variant="danger" onClick={() => { setNotice(null); setConfirming({ kind: 'remove', member: m }); }}>
                          {t('users.remove')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <InviteUserModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSaved={(member, inviteEmailSent) => {
          setInviteOpen(false);
          setNotice(inviteEmailSent
            ? { tone: 'good', message: t('users.inviteSent', { email: member.email }) }
            : { tone: 'warning', message: t('users.inviteNotSent', { email: member.email }) });
          refresh();
        }}
      />
      <EditUserModal
        member={editing}
        isSelf={editing?.id === selfId}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); setNotice({ tone: 'good', message: t('users.saved') }); refresh(); }}
      />
      <ConfirmModal
        target={confirming}
        onClose={() => setConfirming(null)}
        onDone={(message) => { setConfirming(null); setNotice({ tone: 'good', message }); refresh(); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

function InviteUserModal({ open, onClose, onSaved }: {
  open: boolean; onClose: () => void;
  onSaved: (member: Member, inviteEmailSent: boolean) => void;
}) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const blank = { email: '', firstName: '', lastName: '', role: 'EMPLOYEE' as Role, phone: '', location: '' };
  const [form, setForm] = useState(blank);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    // No password and no organisation id are sent: the server generates the
    // one and takes the other from the session.
    mutationFn: () =>
      api.post<{ user: Member; inviteEmailSent: boolean }>('/users', {
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
        role: form.role,
        phone: form.phone || null,
        location: form.location || null,
      }),
    onSuccess: (result) => {
      setForm(blank);
      setError(null);
      onSaved(result.user, result.inviteEmailSent);
    },
    onError: (err) => setError(describeError(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('users.invite')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary"
                  disabled={!form.email.trim() || !form.firstName.trim() || !form.lastName.trim() || create.isPending}
                  onClick={() => create.mutate()}>
            {create.isPending ? t('common.saving') : t('users.sendInvite')}
          </Button>
        </>
      }
    >
      {error && <div className="mb-4"><ErrorNotice message={error} /></div>}

      <p className="oc-muted mb-4">{t('users.inviteHint')}</p>

      <Field label={t('users.email')} htmlFor="u-email" required>
        <input id="u-email" type="email" className="oc-input" value={form.email}
               maxLength={320} autoCapitalize="none" autoCorrect="off"
               onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </Field>
      <Field label={t('users.firstName')} htmlFor="u-first" required>
        <input id="u-first" className="oc-input" value={form.firstName} maxLength={100}
               onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
      </Field>
      <Field label={t('users.lastName')} htmlFor="u-last" required>
        <input id="u-last" className="oc-input" value={form.lastName} maxLength={100}
               onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
      </Field>
      <Field label={t('users.role')} htmlFor="u-role">
        <select id="u-role" className="oc-input" value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
          {ROLES.map((r) => <option key={r} value={r}>{t(`role.${r}`)}</option>)}
        </select>
      </Field>
      <Field label={t('users.phone')} htmlFor="u-phone" hint={t('common.optional')}>
        <input id="u-phone" type="tel" className="oc-input" value={form.phone} maxLength={40}
               onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      </Field>
      <Field label={t('users.location')} htmlFor="u-loc" hint={t('common.optional')}>
        <input id="u-loc" className="oc-input" value={form.location} maxLength={191}
               onChange={(e) => setForm({ ...form, location: e.target.value })} />
      </Field>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

function EditUserModal({ member, isSelf, onClose, onSaved }: {
  member: Member | null; isSelf: boolean; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const [form, setForm] = useState<{ role: Role; phone: string; location: string; isActive: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  // Reset the form whenever a different person is opened. Done during render
  // rather than in an effect so the first paint already shows the right values.
  if (member && loadedFor !== member.id) {
    setLoadedFor(member.id);
    setForm({
      role: member.role,
      phone: member.phone ?? '',
      location: member.location ?? '',
      isActive: member.isActive,
    });
    setError(null);
  }
  if (!member && loadedFor !== null) setLoadedFor(null);

  const update = useMutation({
    mutationFn: () => {
      if (!member || !form) throw new Error('no member');
      // Only what changed is sent, so an untouched field can never be refused
      // (or overwritten) on account of a rule that does not concern it.
      const patch: Record<string, unknown> = {};
      if (form.role !== member.role) patch.role = form.role;
      if (form.phone !== (member.phone ?? '')) patch.phone = form.phone || null;
      if (form.location !== (member.location ?? '')) patch.location = form.location || null;
      if (form.isActive !== member.isActive) patch.isActive = form.isActive;
      return api.patch(`/users/${member.id}`, patch);
    },
    onSuccess: () => { setError(null); onSaved(); },
    onError: (err) => setError(describeError(err)),
  });

  if (!member || !form) return null;

  const dirty =
    form.role !== member.role ||
    form.phone !== (member.phone ?? '') ||
    form.location !== (member.location ?? '') ||
    form.isActive !== member.isActive;

  return (
    <Modal
      open
      onClose={onClose}
      title={`${t('common.edit')} · ${member.firstName} ${member.lastName}`}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={!dirty || update.isPending} onClick={() => update.mutate()}>
            {update.isPending ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      {error && <div className="mb-4"><ErrorNotice message={error} /></div>}

      <Field label={t('users.role')} htmlFor="e-role" hint={isSelf ? t('users.ownRoleHint') : undefined}>
        <select id="e-role" className="oc-input" value={form.role} disabled={isSelf}
                onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
          {ROLES.map((r) => <option key={r} value={r}>{t(`role.${r}`)}</option>)}
        </select>
      </Field>

      {member.sharedAccount && <p className="oc-muted mb-4">{t('users.sharedAccountNote')}</p>}

      <Field label={t('users.phone')} htmlFor="e-phone" hint={t('common.optional')}>
        <input id="e-phone" type="tel" className="oc-input" value={form.phone} maxLength={40}
               disabled={member.sharedAccount}
               onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      </Field>
      <Field label={t('users.location')} htmlFor="e-loc" hint={t('common.optional')}>
        <input id="e-loc" className="oc-input" value={form.location} maxLength={191}
               disabled={member.sharedAccount}
               onChange={(e) => setForm({ ...form, location: e.target.value })} />
      </Field>

      <label className="flex items-start gap-3 text-sm text-ink">
        <input type="checkbox" className="mt-1" checked={form.isActive}
               disabled={isSelf || member.sharedAccount}
               onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
        <span>
          {t('users.activeAccount')}
          <span className="block oc-muted">{t('users.activeHint')}</span>
        </span>
      </label>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/**
 * One confirm dialog for the three actions that are hard or awkward to undo:
 * password reset, MFA reset and removal. They share a shape (name the person,
 * say what will happen, ask once), so they share a component.
 */
function ConfirmModal({ target, onClose, onDone }: {
  target: { kind: ConfirmKind; member: Member } | null;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: () => {
      if (!target) throw new Error('no target');
      const { kind, member } = target;
      if (kind === 'password') return api.post(`/users/${member.id}/password-reset`);
      if (kind === 'mfa') return api.post(`/users/${member.id}/mfa-reset`);
      return api.delete(`/users/${member.id}`);
    },
    onSuccess: () => {
      if (!target) return;
      setError(null);
      const name = `${target.member.firstName} ${target.member.lastName}`;
      onDone(t(`users.done.${target.kind}`, { name }));
    },
    onError: (err) => setError(describeError(err)),
  });

  if (!target) return null;
  const { kind, member } = target;
  const name = `${member.firstName} ${member.lastName}`;

  const close = () => { setError(null); onClose(); };

  return (
    <Modal
      open
      onClose={close}
      title={t(`users.confirm.${kind}.title`)}
      footer={
        <>
          <Button onClick={close}>{t('common.cancel')}</Button>
          <Button variant={kind === 'password' ? 'primary' : 'danger'} disabled={run.isPending}
                  onClick={() => run.mutate()}>
            {run.isPending ? t('common.saving') : t(`users.confirm.${kind}.action`)}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorNotice message={error} />}
        <p className="text-ink">{t(`users.confirm.${kind}.body`, { name, email: member.email })}</p>
        <p className="oc-muted">{t(`users.confirm.${kind}.note`)}</p>
      </div>
    </Modal>
  );
}
